// « Ma journée » (conception, § 6) : en tête de l'accueil du téléphone et du tableau de bord, au-dessus des 10 du
// jour. Chaque ligne mène à la fiche, dont le bloc de suivi porte « Fait » ; l'échéance se change sur place.
import { useQuery } from "@tanstack/react-query";
import { useLocaleState, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/card";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Deal, MyDayRow } from "../types";
import { DealSuggestions } from "./DealSuggestions";
import { today } from "./nextAction";
import {
  LinkedInMessageCopy,
  NextActionEditor,
  nextActionText,
} from "./NextActionEditor";
import { formatDay } from "./projectUtils";
import { useInvited } from "./useInvited";

const BUCKETS: MyDayRow["bucket"][] = [
  "overdue",
  "today",
  "replies",
  "no_action",
];

export const MyDay = () => {
  const translate = useTranslate();
  const invited = useInvited();
  const [locale] = useLocaleState();
  const t = (key: string, options?: object) =>
    translate(`resources.deals.my_day.${key}`, options);
  const {
    data: rows,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["my_day"],
    queryFn: async () => {
      // Le jour de l'utilisateur, pas celui du serveur (UTC) : le soir, à l'est de Greenwich, ce n'est plus le même.
      const { data, error } = await getSupabaseClient().rpc("my_day", {
        p_today: today(),
      });
      if (error) throw error;
      return (data ?? []) as MyDayRow[];
    },
  });
  if (isPending) return null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <h3 className="text-base font-semibold">{t("title")}</h3>
        <DealSuggestions
          dealIds={[...new Set((rows ?? []).map((r) => r.deal_id))]}
          canWrite={!invited}
        />
        {/* Une erreur n'est pas une journée vide (par exemple, l'interface en avance sur la base). */}
        {isError ? (
          <p className="text-sm text-destructive">{t("error")}</p>
        ) : !rows?.length ? (
          <p className="text-sm text-muted-foreground">{t("empty")}</p>
        ) : (
          BUCKETS.map((bucket) => {
            const list = rows.filter((row) => row.bucket === bucket);
            if (!list.length) return null;
            return (
              <section key={bucket} className="flex flex-col gap-1">
                <h4
                  className={`text-xs font-medium uppercase tracking-wide ${bucket === "overdue" ? "text-destructive" : "text-muted-foreground"}`}
                >
                  {t(bucket)} ({list.length})
                </h4>
                <ul className="flex flex-col divide-y">
                  {list.map((row) => (
                    <li
                      key={`${bucket}-${row.deal_id}`}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm"
                    >
                      <Link
                        to={
                          row.project_id
                            ? `/projects/${row.project_id}/show`
                            : `/deals/${row.deal_id}/show`
                        }
                        className="flex-1 min-w-0 font-medium underline-offset-2 hover:underline"
                      >
                        <span className="line-clamp-1">{row.deal_name}</span>
                      </Link>
                      <span className="text-muted-foreground">
                        {bucket === "replies"
                          ? row.contact_name
                            ? t("replied", { name: row.contact_name })
                            : t("replied_company")
                          : [
                              nextActionText(
                                row as unknown as Pick<
                                  Deal,
                                  "next_action_type" | "next_action"
                                >,
                                translate,
                              ),
                              row.next_action_date &&
                                formatDay(row.next_action_date, locale),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                      </span>
                      {row.next_action_type === "linkedin_dm" &&
                        row.next_action_message && (
                          <LinkedInMessageCopy
                            message={row.next_action_message}
                          />
                        )}
                      {!invited && (
                        <NextActionEditor
                          deal={
                            {
                              id: row.deal_id,
                              next_action_type: row.next_action_type,
                              next_action: row.next_action,
                              next_action_date: row.next_action_date,
                              next_action_contact_id: row.contact_id,
                            } as Deal
                          }
                          compact
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};
